import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";

// Centrale registratie voor de sticky save-bar: één actieve editor per pagina
// meldt zijn aantal wijzigingen + save/discard-handlers aan; de bar rendert in AppShell.
const SaveBarContext = createContext(null);

export function SaveBarProvider({ children }) {
  const [entry, setEntry] = useState(null); // { id, count, saving }
  const handlersRef = useRef({}); // id -> { onSave, onDiscard }

  const register = useCallback((id, { count, saving, onSave, onDiscard }) => {
    handlersRef.current[id] = { onSave, onDiscard };
    setEntry((prev) => {
      if (prev && prev.id !== id && prev.count > 0 && count === 0) return prev;
      // Bail-out als er niets verandert: anders levert elke register-call een
      // nieuw entry-object → nieuwe context-value → useSaveBar-effect herfireert →
      // register opnieuw → oneindige render-lus die react-router's location-
      // startTransition uithongert (URL verandert wel, <Routes> re-rendert niet).
      if (prev && prev.id === id && prev.count === count && prev.saving === !!saving) {
        return prev;
      }
      return { id, count, saving: !!saving };
    });
  }, []);

  const unregister = useCallback((id) => {
    delete handlersRef.current[id];
    setEntry((prev) => (prev && prev.id === id ? null : prev));
  }, []);

  const save = useCallback(() => {
    if (entry) handlersRef.current[entry.id]?.onSave?.();
  }, [entry]);

  const discard = useCallback(() => {
    if (entry) handlersRef.current[entry.id]?.onDiscard?.();
  }, [entry]);

  const value = useMemo(
    () => ({ entry, register, unregister, save, discard }),
    [entry, register, unregister, save, discard]
  );

  return <SaveBarContext.Provider value={value}>{children}</SaveBarContext.Provider>;
}

// Hook voor editor-pagina's: koppelt bestaande changedCells-state aan de save-bar.
export function useSaveBar({ count = 0, saving = false, onSave, onDiscard }) {
  const ctx = useContext(SaveBarContext);
  // register/unregister zijn useCallback([]) → stabiele identiteit. Key de effects
  // op die functies (niet op de hele ctx), zodat een gewijzigde entry (nieuwe ctx-
  // value) de register-effect NIET opnieuw laat vuren. Dat samen met de bail-out in
  // register voorkomt de render-lus die navigatie op tabel-editors blokkeerde.
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  // Stabiele, unieke id per hook-instance zonder module-globale mutatie tijdens render.
  const id = useId();

  const onSaveRef = useRef(onSave);
  const onDiscardRef = useRef(onDiscard);
  onSaveRef.current = onSave;
  onDiscardRef.current = onDiscard;

  useEffect(() => {
    if (!register) return undefined;
    register(id, {
      count,
      saving,
      onSave: () => onSaveRef.current?.(),
      onDiscard: () => onDiscardRef.current?.(),
    });
    return undefined;
  }, [register, id, count, saving]);

  useEffect(() => {
    if (!unregister) return undefined;
    return () => unregister(id);
  }, [unregister, id]);
}

export function useSaveBarState() {
  return useContext(SaveBarContext);
}
